import React, { useState, useRef, useEffect, useCallback } from "react";

const VISIBLE_SIDE = 2;

function useScreenType() {
  const [screenType, setScreenType] = useState("desktop");

  useEffect(() => {
    const handler = () => {
      if (window.innerWidth < 600) setScreenType("mobile");
      else if (window.innerWidth <= 900) setScreenType("tablet");
      else setScreenType("desktop");
    };
    handler(); // init
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return screenType;
}

function getSlideStyle(offset, screenType) {
  const absOffset = Math.abs(offset);
  const sign = offset >= 0 ? 1 : -1;

  const xStep =
    screenType === "mobile" ? 160 : screenType === "tablet" ? 210 : 260;
  const zStep =
    screenType === "mobile" ? 50 : screenType === "tablet" ? 80 : 110;
  const rotStep =
    screenType === "mobile" ? 32 : screenType === "tablet" ? 38 : 42;
  const scaleStep = screenType === "mobile" ? 0.1 : 0.12;
  const opacityStep = screenType === "mobile" ? 0.28 : 0.35;

  const clampedAbs = Math.min(absOffset, VISIBLE_SIDE + 1);

  const z = -clampedAbs * zStep;
  const x = sign * clampedAbs * xStep;
  const rotY = -sign * clampedAbs * rotStep;
  const scale = (absOffset === 0 ? 1.12 : 1) - clampedAbs * scaleStep;
  const opacity = absOffset > VISIBLE_SIDE ? 0 : 1 - absOffset * opacityStep;
  const blur = clampedAbs * 1.5;

  // На мобильных blur не используем (тяжёлый для GPU), только opacity
  const useBlur = screenType !== "mobile";

  return {
    transform: `translateX(${x}px) translateZ(${z}px) rotateY(${rotY}deg) scale(${scale})`,
    opacity,
    filter: useBlur
      ? `blur(${blur}px)`
      : absOffset > 0
        ? `brightness(${1 - clampedAbs * 0.2})`
        : "none",
    zIndex: 10 - clampedAbs,
    pointerEvents: absOffset > VISIBLE_SIDE ? "none" : "auto",
    cursor: absOffset === 0 ? undefined : "pointer",
  };
}

function SlideBackground({ slide, isFront }) {
  // Поддержка обоих форматов: slide.image (ручное) и slide.image_url (из БД)
  const rawUrl = slide.image || slide.image_url || "";
  const [videoFailed, setVideoFailed] = useState(false);

  // Определяем, нужна ли замена .gif → .mp4
  const isGif = rawUrl.toLowerCase().endsWith(".gif");
  const mp4Url = isGif ? rawUrl.replace(/\.gif$/i, ".mp4") : null;
  // Итоговый URL: если это GIF и mp4 не провалился — используем mp4 через прокси, иначе оригинал
  const directUrl = isGif && !videoFailed && mp4Url ? mp4Url : rawUrl;
  const isVideo = directUrl.toLowerCase().endsWith(".mp4");
  // Для видео используем прокси (CSP Telegram блокирует media-src с внешних доменов)
  const imageUrl = isVideo
    ? `/api/content/media-proxy?url=${encodeURIComponent(directUrl)}`
    : directUrl;

  const videoRef = useRef(null);
  const [isInViewport, setIsInViewport] = useState(true);

  const playVideo = useCallback((video) => {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch((e) => console.log("Autoplay prevented:", e));
    }
  }, []);

  const resumeVideo = useCallback(() => {
    const video = videoRef.current;
    if (
      !video ||
      !isVideo ||
      !isFront ||
      !isInViewport ||
      (typeof document !== "undefined" && document.hidden)
    ) {
      return;
    }

    playVideo(video);
  }, [isFront, isInViewport, isVideo, playVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (isVideo && video) {
      if (isFront) {
        video.currentTime = 0;
        playVideo(video);
      } else {
        video.pause();
      }
    }
  }, [isFront, isVideo, playVideo]);

  useEffect(() => {
    if (!isVideo || typeof IntersectionObserver === "undefined") return;

    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInViewport(entry.isIntersecting && entry.intersectionRatio > 0.35);
      },
      { threshold: [0, 0.35, 0.75] },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [imageUrl, isVideo]);

  useEffect(() => {
    if (!isVideo || !isFront) return;

    const handleForeground = () => {
      if (typeof document !== "undefined" && document.hidden) {
        return;
      }
      resumeVideo();
    };

    window.addEventListener("focus", handleForeground);
    window.addEventListener("pageshow", handleForeground);
    document.addEventListener("visibilitychange", handleForeground);

    return () => {
      window.removeEventListener("focus", handleForeground);
      window.removeEventListener("pageshow", handleForeground);
      document.removeEventListener("visibilitychange", handleForeground);
    };
  }, [isFront, isVideo, resumeVideo]);

  useEffect(() => {
    if (isFront && isInViewport) {
      resumeVideo();
    }
  }, [isFront, isInViewport, resumeVideo]);

  // Сбрасываем ошибку при смене слайда
  useEffect(() => {
    setVideoFailed(false);
  }, [rawUrl]);

  if (!imageUrl) return null;

  const style = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    opacity: 1,
    zIndex: 0,
    pointerEvents: "none",
  };

  if (isVideo) {
    return (
      <video
        ref={videoRef}
        src={imageUrl}
        autoPlay
        loop
        muted
        playsInline
        style={style}
        onError={() => setVideoFailed(true)}
        onPause={() => {
          if (!isFront) return;
          requestAnimationFrame(() => {
            resumeVideo();
          });
        }}
      />
    );
  }
  return (
    <img
      src={imageUrl}
      alt={slide.label || slide.title || "Card"}
      style={style}
    />
  );
}

export default function TopCarousel({ items = [], onItemClick }) {
  const screenType = useScreenType();
  const [current, setCurrent] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragDelta, setDragDelta] = useState(0);
  const [animating, setAnimating] = useState(false);
  const autoplayRef = useRef(null);
  const containerRef = useRef(null);

  // 10 дефолтных цветов для свечения фона
  const defaultAccents = [
    "#7b5ea7", // Nebula purple
    "#e74c3c", // Ember red
    "#0074d9", // Arctic blue
    "#2ecc71", // Forest green
    "#f39c12", // Aurora orange
    "#ffd700", // Solstice gold
    "#00e5cc", // Abyss cyan
    "#ff4da6", // Bloom pink
    "#ff6a00", // Forge orange-red
    "#a8ff78", // Prism light green
  ];

  const goTo = useCallback(
    (index) => {
      if (animating || items.length === 0) return;
      setAnimating(true);
      setCurrent((index + items.length) % items.length);
      setTimeout(() => setAnimating(false), 600);
    },
    [animating, items.length],
  );

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (items.length > 0) {
      autoplayRef.current = setInterval(next, 7000);
    }
    return () => clearInterval(autoplayRef.current);
  }, [next, items.length]);

  const resetAutoplay = () => {
    clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(next, 7000);
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowRight") {
        next();
        resetAutoplay();
      }
      if (e.key === "ArrowLeft") {
        prev();
        resetAutoplay();
      }
    },
    [next, prev],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const onPointerDown = (e) => {
    setDragging(true);
    setDragStart(e.clientX);
    setDragDelta(0);
    clearInterval(autoplayRef.current);
    // Pointer capture только для touch — на десктопе это блокирует onClick на карточках
    if (e.pointerType === "touch") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    setDragDelta(e.clientX - dragStart);
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (dragDelta < -60) {
      next();
      resetAutoplay();
    } else if (dragDelta > 60) {
      prev();
      resetAutoplay();
    } else resetAutoplay();
    setDragDelta(0);
  };

  if (!items || items.length === 0) return null;

  const activeSlide = items[current] || items[0];
  // Берем цвет из карточки, а если его нет - применяем дефолтный цвет по индексу массива (с закольцовкой)
  const accentColor =
    activeSlide?.accent || defaultAccents[current % defaultAccents.length];

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: "0px",
        position: "relative",
        userSelect: "none",
        zIndex: 1, // Гарантируем, что скролл контента будет работать корректно
      }}
    >
      <style>{`
        .tc-slide-card {
          position: absolute;
          width: 480px;      
          height: 295px;     
          border-radius: 20px;
          background: #171719 !important; 
          transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1),
                      opacity 0.6s cubic-bezier(0.23, 1, 0.32, 1),
                      filter 0.6s cubic-bezier(0.23, 1, 0.32, 1);
          will-change: transform, opacity;
          overflow: hidden;
          cursor: grab;
        }
        .tc-slide-card:active { cursor: grabbing; }

        .tc-card-gradient {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(23, 23, 25, 1) 10%, rgba(23, 23, 25, 0.8) 25%, rgba(23, 23, 25, 0) 45%);
          pointer-events: none;
        }

        .tc-card-info {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          padding: 24px;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          pointer-events: none;
        }

        .tc-card-title {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          font-size: 17px;
          font-weight: 700;
          color: #f0f0f0;
          line-height: 1.35;
          margin: 0;
          text-align: left;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 0 10px rgba(0, 0, 0, 0.5);
        }

        .tc-glow-bg {
          position: absolute;
          inset: -30%;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.15;
          transition: background 1.2s ease, opacity 0.6s ease;
          pointer-events: none;
          z-index: 0;
        }

        .tc-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
          cursor: pointer;
        }
        .tc-dot.active {
          width: 20px;
          border-radius: 4px;
          background: white;
        }

        .tc-divider {
          width: 100%;
          max-width: 480px;
          padding: 0 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          z-index: 3;
          margin-bottom: 24px;
        }

        @media (min-width: 640px) {
          .tc-divider { padding: 0 32px; }
        }

        .tc-divider-label {
          flex-shrink: 0;
          font-family: 'Barlow Condensed', "Arial Narrow", sans-serif;
          font-weight: 800;
          font-size: 16px;
          letter-spacing: 4px;
          text-transform: uppercase;
        }

        .tc-aurora-text {
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: aurora 8s ease-in-out infinite alternate;
          display: inline-block;
        }

        .tc-color-top {
          background-image: linear-gradient(135deg, #d32f2f 0%, #ff6a00 33%, #fbc02d 66%, #d32f2f 100%);
        }

        .tc-color-new {
          background-image: linear-gradient(135deg, #0ba360 0%, #3cba92 33%, #a8ff78 66%, #0ba360 100%);
        }

        .tc-divider-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, rgba(220, 130, 20, 0.9) 0%, rgba(200, 60, 10, 0.4) 55%, transparent 100%);
          position: relative;
          animation: linePulse 3s ease-in-out infinite;
        }
        .tc-divider-line::after {
          content: ''; position: absolute; inset: -1px 0; background: inherit; filter: blur(3px); opacity: 0.55;
        }

        .tc-divider-line-left {
          display: block;
          flex: 1;
          height: 1px;
          background: linear-gradient(270deg, rgba(220, 130, 20, 0.9) 0%, rgba(200, 60, 10, 0.4) 55%, transparent 100%);
          position: relative;
          animation: linePulse 3s ease-in-out infinite;
        }
        .tc-divider-line-left::after {
          content: ''; position: absolute; inset: -1px 0; background: inherit; filter: blur(3px); opacity: 0.55;
        }

        @keyframes linePulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }

        @keyframes aurora {
          0% { background-position: 0% 50%; }
          25% { background-position: 50% 100%; }
          50% { background-position: 100% 50%; }
          75% { background-position: 50% 0%; }
          100% { background-position: 0% 50%; }
        }

        .tc-card-border-base {
          position: absolute; inset: 0; border-radius: inherit; border: 1px solid rgba(255,255,255,0.05); pointer-events: none;
        }

        .tc-card-border {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 2px;
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
          background-image: radial-gradient(transparent, transparent, #A07CFE, #FE8FB5, #FFBE7B, transparent, transparent);
          background-size: 300% 300%;
          animation: shine 14s infinite linear;
          will-change: background-position;
        }

        @keyframes shine {
          0% { background-position: 0% 0%; }
          50% { background-position: 100% 100%; }
          to { background-position: 0% 0%; }
        }

        @media (max-width: 900px) and (min-width: 601px) {
          .tc-slide-card {
            width: 352px !important;  
            height: 216px !important; 
            border-radius: 16px !important;
          }
          .tc-card-title { font-size: 14px; }
          .tc-card-info { padding: 18px; }
        }

        @media (max-width: 600px) {
          .tc-slide-card {
            width: 256px !important; 
            height: 160px !important;
            border-radius: 12px !important;
            /* На мобильных отключаем тяжёлые анимации и filter transition */
            transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1),
                        opacity 0.6s cubic-bezier(0.23, 1, 0.32, 1);
          }
          .tc-card-title { font-size: 12px; }
          .tc-card-info { padding: 14px; }
          .tc-glow-bg { filter: blur(40px) !important; }
          .tc-card-border { animation: none; }
          .tc-card-border.tc-border-active { animation: shine 14s infinite linear; }
        }
      `}</style>

      {/* Ambient glow */}
      <div
        className="tc-glow-bg"
        style={{
          background: `radial-gradient(ellipse at center, ${accentColor} 0%, transparent 70%)`,
        }}
      />

      {/* Top 10 Divider */}
      <div className="tc-divider" style={{ marginTop: 4 }}>
        <div className="tc-divider-line-left"></div>
        <span className="tc-divider-label tc-aurora-text tc-color-top">
          TOP
        </span>
        <div className="tc-divider-line"></div>
      </div>

      {/* 3D Stage */}
      <div
        ref={containerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          position: "relative",
          width: "100%",
          height:
            screenType === "mobile" ? 180 : screenType === "tablet" ? 280 : 360,
          perspective: "1200px",
          perspectiveOrigin: "50% 50%",
          transformStyle: "preserve-3d",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2,
          touchAction: "pan-y",
        }}
      >
        {items.map((slide, i) => {
          let offset = i - current;
          if (offset > items.length / 2) offset -= items.length;
          if (offset < -items.length / 2) offset += items.length;

          const style = getSlideStyle(offset, screenType);
          const isFront = offset === 0;

          return (
            <div
              key={slide.id}
              className="tc-slide-card"
              style={{
                ...style,
              }}
              onClick={() => {
                if (offset !== 0 && Math.abs(dragDelta) < 5) {
                  goTo(i);
                  resetAutoplay();
                } else if (offset === 0 && Math.abs(dragDelta) < 5) {
                  // Клик по центральной, передней карточке
                  if (onItemClick) onItemClick(slide);
                }
              }}
            >
              <SlideBackground slide={slide} isFront={isFront} />
              <div className="tc-card-gradient" style={{ zIndex: 1 }} />
              <div className="tc-card-info" style={{ zIndex: 2 }}>
                <h3 className="tc-card-title">
                  {slide.label || slide.title || "No Title"}
                </h3>
              </div>
              <div className="tc-card-border-base" style={{ zIndex: 5 }} />
              <div
                className={`tc-card-border${isFront ? " tc-border-active" : ""}`}
                style={{ zIndex: 6 }}
              />
            </div>
          );
        })}
      </div>

      {/* Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: screenType === "mobile" ? 16 : 28,
          marginTop: screenType === "mobile" ? 16 : 0,
          zIndex: 3,
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {items.map((_, i) => (
            <div
              key={i}
              className={`tc-dot ${i === current ? "active" : ""}`}
              onClick={() => {
                goTo(i);
                resetAutoplay();
              }}
            />
          ))}
        </div>
      </div>

      {/* NEW Divider */}
      <div className="tc-divider" style={{ marginTop: 24, marginBottom: 20 }}>
        <div className="tc-divider-line-left"></div>
        <span className="tc-divider-label tc-aurora-text tc-color-new">
          NEW
        </span>
        <div className="tc-divider-line"></div>
      </div>
    </div>
  );
}
